import { useTranslation } from "react-i18next";
import { ORDER_ITEM_KINDS, ORDER_ITEM_KIND_LABELS, type OrderItemKind } from "@ckgo2/shared/types";
import { formatPrice } from "@ckgo2/shared/utils";
import { enumLabel } from "@/i18n/enumLabels";
import { Plus, Trash2, Tag } from "lucide-react";

export interface OrderItemRow {
  id?: string;
  kind: OrderItemKind;
  tripId?: string | null;
  tripDateId?: string | null;
  name: string;
  description?: string | null;
  unitPrice: number;
  quantity: number;
  currency?: string;
  sortOrder?: number;
}

interface OrderItemsEditorProps {
  value: OrderItemRow[];
  onChange: (items: OrderItemRow[]) => void;
  currency?: string;
}

/**
 * Editable order line items (vision §5.3). Each row is a snapshot: editing a
 * trip-row's name/price overrides it for THIS order only — the catalog trip is
 * never touched. Add free custom/addon/discount rows. The footer shows the live
 * total (the server recomputes the authoritative value on save).
 */
export default function OrderItemsEditor({
  value,
  onChange,
  currency = "CZK",
}: OrderItemsEditorProps) {
  const { t } = useTranslation();
  const items = value ?? [];

  const update = (idx: number, patch: Partial<OrderItemRow>) => {
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx));

  const add = (kind: OrderItemKind) => {
    onChange([
      ...items,
      {
        kind,
        name: kind === "discount" ? enumLabel(ORDER_ITEM_KIND_LABELS, "discount") : "",
        unitPrice: 0,
        quantity: 1,
        currency,
        sortOrder: items.length,
      },
    ]);
  };

  const total = items.reduce(
    (sum, it) => sum + (Number(it.unitPrice) || 0) * (Number(it.quantity) || 1),
    0,
  );

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-400">
              <th className="pb-2 pr-2">{t("common.fields.type")}</th>
              <th className="pb-2 pr-2">{t("common.fields.description")}</th>
              <th className="pb-2 pr-2 text-right">{t("shared.orderItems.unitPrice")}</th>
              <th className="pb-2 pr-2 text-right">{t("shared.orderItems.qty")}</th>
              <th className="pb-2 pr-2 text-right">{t("shared.orderItems.lineTotal")}</th>
              <th className="pb-2" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="py-4 text-center text-gray-400">
                  {t("shared.orderItems.empty")}
                </td>
              </tr>
            )}
            {items.map((it, idx) => {
              const lineTotal = (Number(it.unitPrice) || 0) * (Number(it.quantity) || 1);
              return (
                <tr key={it.id ?? idx} className="border-t border-gray-100 dark:border-gray-700/60">
                  <td className="py-2 pr-2 align-top">
                    <select
                      value={it.kind}
                      onChange={(e) => update(idx, { kind: e.target.value as OrderItemKind })}
                      className="input !py-1.5 text-sm"
                    >
                      {ORDER_ITEM_KINDS.map((k) => (
                        <option key={k} value={k}>
                          {enumLabel(ORDER_ITEM_KIND_LABELS, k)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 pr-2 align-top">
                    <input
                      value={it.name}
                      onChange={(e) => update(idx, { name: e.target.value })}
                      placeholder={t("shared.orderItems.itemNamePlaceholder")}
                      className="input !py-1.5 text-sm"
                    />
                    {it.kind === "trip" && it.tripId && (
                      <span className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-400">
                        <Tag className="h-3 w-3" />
                        {t("shared.orderItems.priceOverride")}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-2 align-top">
                    <input
                      type="number"
                      value={it.unitPrice}
                      onChange={(e) => update(idx, { unitPrice: Number(e.target.value) })}
                      className="input w-28 !py-1.5 text-right text-sm"
                    />
                  </td>
                  <td className="py-2 pr-2 align-top">
                    <input
                      type="number"
                      min={1}
                      value={it.quantity}
                      onChange={(e) => update(idx, { quantity: Number(e.target.value) })}
                      className="input w-16 !py-1.5 text-right text-sm"
                    />
                  </td>
                  <td className="py-2 pr-2 text-right align-middle font-medium tabular-nums text-gray-900 dark:text-white">
                    {formatPrice(lineTotal, currency)}
                  </td>
                  <td className="py-2 align-middle">
                    <button
                      title={t("common.actions.remove")}
                      type="button"
                      onClick={() => remove(idx)}
                      className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 dark:border-gray-600">
              <td colSpan={4} className="pt-2 text-right text-sm font-medium text-gray-500">
                {t("common.fields.total")}
              </td>
              <td className="pt-2 text-right text-base font-bold tabular-nums text-gray-900 dark:text-white">
                {formatPrice(total, currency)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => add("custom")} className="btn-ghost btn-sm">
          <Plus className="h-4 w-4" />
          {t("shared.orderItems.addLine")}
        </button>
        <button type="button" onClick={() => add("addon")} className="btn-ghost btn-sm">
          <Plus className="h-4 w-4" />
          {t("shared.orderItems.addAddon")}
        </button>
        <button type="button" onClick={() => add("discount")} className="btn-ghost btn-sm">
          <Plus className="h-4 w-4" />
          {t("shared.orderItems.addDiscount")}
        </button>
      </div>
    </div>
  );
}
